import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Mail } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { getAllEmployeesIsActive } from "../../../api/hrm/employees/employeeInfo";
import { notifySigningRecipients } from "../../../api/hrm/signing/signingRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";

const SigningNotifyModal = ({ open, requestId, onClose, onSent }) => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [keyword, setKeyword] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await getAllEmployeesIsActive();
        setEmployees(Array.isArray(res) ? res : res?.records || []);
      } catch {
        setEmployees([]);
      }
    })();
    setSelected(new Set());
    setKeyword("");
  }, [open]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return employees;
    return employees.filter(
      (e) =>
        e.name?.toLowerCase().includes(kw) ||
        e.email?.toLowerCase().includes(kw)
    );
  }, [employees, keyword]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (selected.size === 0) {
      showErrorToast("Chọn ít nhất một nhân viên.");
      return;
    }
    setSending(true);
    try {
      await notifySigningRecipients(requestId, Array.from(selected));
      showSuccessToast(`Đã gửi thông báo cho ${selected.size} nhân viên.`);
      onSent?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Lỗi khi gửi email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="font-black uppercase tracking-tight text-gray-800 flex items-center gap-2">
                  <Mail size={18} className="text-red-700" />
                  Gửi email thông báo
                </h3>
                <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100">
                  <X size={18} />
                </button>
              </div>

              <div className="p-4 border-b border-gray-100">
                <div className="relative group">
                  <Search
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500"
                  />
                  <input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Tìm nhân viên..."
                    className="w-full bg-gray-50 border border-gray-200 pl-11 pr-4 py-3 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
                  />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-2">
                  Đã chọn: {selected.size}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {filtered.map((emp) => (
                  <label
                    key={emp.id}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-red-50/50 cursor-pointer border border-transparent hover:border-red-100 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(emp.id)}
                      onChange={() => toggle(emp.id)}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{emp.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">
                        {emp.personal_email || emp.email}
                      </p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="p-4 border-t border-gray-100 flex gap-3 justify-end bg-gray-50/50">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-full border border-gray-200 text-gray-600"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full text-xs font-bold uppercase tracking-wider disabled:opacity-50 hover:shadow-lg hover:shadow-red-100 transition-all"
                >
                  {sending ? "Đang gửi..." : "Gửi email"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SigningNotifyModal;
